import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import i18n, { getLanguageName, languageOptions } from 'locale';
import { useProgramPanel } from 'contexts/programPanelContext';
import { DialogAction } from 'components/_new/Dialog';
import { SurveyProgramQuestionBase, SurveyQuestionType } from 'types/Survey';
import { ApplicationRole } from 'types/Application';
import { Field } from 'components/_new/Field';
import { Button } from 'components/_new/Button';
import { capitalizeWord } from 'utils/helpers';
import { Dropdown } from 'components/_new/Dropdown';
import { Checkbox } from 'primereact/checkbox';
import { Tag } from 'components/_new/Tag';
import { Divider } from 'components/_new/Divider';
import { ButtonGroup } from 'components/_new/ButtonGroup/ButtonGroup';
import { ModifyQuestionPayload } from 'api/survey';
import { InputText } from 'components/_new/InputText';
import { getQuestionTypeOptions } from './utils';

interface QuestionEditDialogProps {
	visible: boolean;
	question: SurveyProgramQuestionBase | null;
	onHide: () => void;
	onSave: (question: ModifyQuestionPayload, questionId?: number) => void;
	disableActions: boolean;
}

type ModifyQuestionForm = {
	text: string;
	type: SurveyQuestionType;
	required: boolean;
	forApplicationRole: ApplicationRole;
	translations: Array<{ languageCode: string; text: string }>;
	options: Array<{
		id?: number;
		text: string;
		sequence: number;
		translations?: Array<{ languageCode: string; text: string }>;
	}>;
};

export const QuestionEditDialog: React.FC<QuestionEditDialogProps> = ({
	visible,
	question,
	onHide,
	onSave,
	disableActions,
}) => {
	const { t } = useTranslation();
	const { data: program } = useProgramPanel();

	const isNewQuestion = Boolean(!question);

	const defaultValues = useMemo((): ModifyQuestionForm => {
		return {
			text: question?.text || '',
			type: question?.type || SurveyQuestionType.TEXT,
			required: question?.required || false,
			forApplicationRole: question?.forApplicationRole || 'peer',
			translations: question?.translations || [],
			options:
				question?.options?.map((opt) => ({
					id: opt.id,
					text: opt.text,
					sequence: opt.sequence,
					translations: opt.translations || [],
				})) || [],
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [question, visible]);

	const {
		control,
		handleSubmit,
		reset,
		watch,
		formState: { errors },
		setValue,
		getValues,
		setError,
		clearErrors,
	} = useForm<ModifyQuestionForm>({
		defaultValues,
	});

	const type = watch('type');
	const isChoiceType = [SurveyQuestionType.SINGLE_CHOICE, SurveyQuestionType.MULTI_CHOICE].includes(type);

	const {
		fields: translationFields,
		append: appendTranslation,
		remove: removeTranslation,
	} = useFieldArray({
		control,
		name: 'translations',
	});

	const {
		fields: optionFields,
		append: appendOption,
		remove: removeOption,
		move: moveOption,
	} = useFieldArray({
		control,
		name: 'options',
	});

	const formOptions = watch('options');

	useEffect(() => {
		reset(defaultValues);
		return () => {
			reset();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [question, visible]);

	useEffect(() => {
		if (isChoiceType && defaultValues?.options.length === 0 && optionFields.length === 0) {
			appendOption({ sequence: 1, text: '', translations: [] });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [defaultValues, isChoiceType, type]);

	const validateOptionTranslations = useCallback(
		(formData: ModifyQuestionForm) => {
			clearErrors('options');
			let hasErrors = false;
			formData.options?.forEach((option, index) => {
				if (option.text.trim() === '') {
					return;
				}

				const translations = option.translations || [];
				translationFields.forEach((translationField) => {
					const translation = translations.find(
						(trans) => trans.languageCode === translationField.languageCode
					);
					if (!translation || translation.text.trim() === '') {
						setError(`options.${index}.text`, {
							type: 'required',
							message: t('programPanel.feedback.optionTranslationRequired'),
						});
						hasErrors = true;
					}
				});
			});

			return !hasErrors;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[translationFields]
	);

	const onSubmit = handleSubmit((data) => {
		if (translationFields.length > 0 && !validateOptionTranslations(data)) {
			return;
		}

		const formattedOptions =
			data.options
				?.filter((opt) => opt.text.trim() !== '')
				.map((opt, index) => ({
					...(opt.id
						? {
								id: opt.id,
						  }
						: {}),
					sequence: index + 1,
					text: opt.text.trim(),
					translations: (opt.translations || []).filter((t) => t.text.trim() !== ''),
				})) || [];

		onSave(
			{
				text: data.text,
				type: data.type,
				required: data.required,
				forApplicationRole: data.forApplicationRole,
				translations: data.translations,
				options: formattedOptions,
			},
			question?.id
		);
	});

	const programLanguageCode = program?.language || 'pl';

	const addTranslation = useCallback(
		(languageCode: string) => {
			const translationExists =
				translationFields.findIndex(({ languageCode: langaugeCode }) => langaugeCode === languageCode) !== -1;
			if (!translationExists && languageCode !== programLanguageCode) {
				appendTranslation({ languageCode, text: '' });
			}
		},
		[translationFields, appendTranslation, programLanguageCode]
	);

	const removeTranslationCallback = useCallback(
		(index: number) => {
			removeTranslation(index);
		},
		[removeTranslation]
	);

	const availableLanguages = useMemo(() => {
		const usedLanguages = [
			programLanguageCode,
			...translationFields.map(({ languageCode: langaugeCode }) => langaugeCode),
		];
		return languageOptions.filter(({ value }) => !usedLanguages.includes(value));
	}, [translationFields, programLanguageCode]);

	const questionTypeOptions = Object.entries(getQuestionTypeOptions(t)).map(([value, label]) => ({ value, label }));

	const applicationRoleOptions = [
		{ value: 'peer' as ApplicationRole, label: t('programPanel.application.applicationRoleFilterOptions.all') },
		{
			value: 'mentor' as ApplicationRole,
			label: t('programPanel.application.applicationRoleFilterOptions.mentor'),
		},
		{
			value: 'mentee' as ApplicationRole,
			label: t('programPanel.application.applicationRoleFilterOptions.mentee'),
		},
	];

	const handleAddOptionClick = () => {
		appendOption({ text: '', sequence: optionFields.length + 1 });
	};

	const handleMoveOption = useCallback(
		(index: number, direction: 'up' | 'down') => {
			if (direction === 'up' && index > 0) {
				moveOption(index, index - 1);
			} else if (direction === 'down' && index < optionFields.length - 1) {
				moveOption(index, index + 1);
			}
		},
		[optionFields.length, moveOption]
	);

	return (
		<DialogAction
			visible={visible}
			onHide={onHide}
			title={t('programPanel.feedback.editQuestion')}
			size="lg"
			actions={[
				{
					key: 'save',
					label: isNewQuestion ? t('actions.add') : t('actions.save'),
					icon: isNewQuestion ? 'plus' : 'save',
					variant: 'primary',
					onClick: onSubmit,
					form: 'add-question-form',
					submit: true,
					loading: disableActions,
				},
			]}
		>
			<form id="add-question-form" className="flex flex-column gap-3">
				<div className="flex flex-column gap-2">
					<Field label={t('programPanel.feedback.questionType')} required errorMessage={errors.type?.message}>
						<Controller
							name="type"
							control={control}
							rules={{ required: t('validation.required') }}
							render={({ field }) => (
								<Dropdown
									{...field}
									options={questionTypeOptions}
									placeholder={t('programPanel.feedback.selectQuestionType')}
								/>
							)}
							disabled={Boolean(question)}
						/>
					</Field>

					<Field label={t('programPanel.feedback.forApplicationRole')}>
						<Controller
							name="forApplicationRole"
							control={control}
							render={({ field }) => <Dropdown {...field} options={applicationRoleOptions} />}
						/>
					</Field>

					<Field
						label={`${t('programPanel.feedback.questionText')} (${getLanguageName(
							programLanguageCode,
							i18n.language
						)})`}
						required
						errorMessage={errors.text?.message}
					>
						<Controller
							name="text"
							control={control}
							rules={{ required: t('validation.required') }}
							render={({ field }) => (
								<InputText
									{...field}
									placeholder={t('programPanel.feedback.questionTextPlaceholder')}
								/>
							)}
						/>
					</Field>

					{translationFields.map((field, index) => {
						const languageName = getLanguageName(field.languageCode, i18n.language);
						return (
							<div key={field.id} className="flex flex-row gap-2 align-items-start">
								<Controller
									name={`translations.${index}.text`}
									control={control}
									rules={{ required: { value: true, message: t('misc.forms.fieldIsRequired') } }}
									render={({ field: textField, fieldState: { error } }) => (
										<Field
											label={`${t('programPanel.feedback.questionText')} (${languageName})`}
											containerClassName="flex-1"
											errorMessage={error?.message}
										>
											<InputText
												{...textField}
												placeholder={t('programPanel.feedback.questionTextPlaceholder')}
												invalid={Boolean(error?.message)}
											/>
										</Field>
									)}
								/>
								{field.languageCode !== programLanguageCode && (
									<Button
										icon="trash"
										iconOnly
										label={t('actions.remove')}
										onClick={() => removeTranslationCallback(index)}
										variant="danger-text"
										variantSize="sm"
										className="mt-4"
									/>
								)}
							</div>
						);
					})}

					{availableLanguages.length > 0 && (
						<div className="flex flex-column gap-2">
							{availableLanguages.map(({ value }) => {
								const languageName = getLanguageName(value, i18n.language);
								return (
									<Button
										key={value}
										label={t('knowledgeBase.programPanel.directoryForm.addTranslation', {
											languageName: capitalizeWord(languageName || value),
										})}
										icon="plus"
										onClick={() => addTranslation(value)}
										variant="primary-outlined"
										variantSize="sm"
									/>
								);
							})}
						</div>
					)}
				</div>

				{isChoiceType && (
					<>
						<Divider />
						<div className="flex flex-column gap-2">
							<span className="font-semibold">{t('programPanel.feedback.options')}</span>

							<ul className="list-none flex flex-column gap-2">
								{optionFields.map((field, index) => {
									const isNewOption = optionFields.length === index + 1 && field.text === '';
									// const currentValue = getValues(`options.${index}`);

									return (
										<li
											key={`option-${field.id}`}
											className="flex flex-row gap-2 align-items-start"
										>
											<div className="min-w-2-5rem mt-2">
												<Tag variant="blue">{index + 1}.</Tag>
											</div>

											<div className="flex-1 flex flex-column">
												<Controller
													name={`options.${index}.text`}
													control={control}
													rules={{
														required: {
															value: true,
															message: t('misc.forms.fieldIsRequired'),
														},
													}}
													render={({ field: textField, fieldState: { error } }) => (
														<Field
															label={
																isNewOption
																	? t('programPanel.feedback.newOption')
																	: `${t('programPanel.feedback.option')} ${
																			index + 1
																	  }`
															}
															className="hidden"
															containerClassName="w-full"
															errorMessage={error?.message}
														>
															<InputText
																{...textField}
																invalid={Boolean(error?.message)}
																placeholder={
																	isNewOption
																		? t(
																				'programPanel.feedback.newOptionPlaceholder'
																		  )
																		: t('programPanel.feedback.optionPlaceholder')
																}
															/>
														</Field>
													)}
												/>

												{translationFields.length > 0 && (
													<ul className="list-none flex flex-column gap-1">
														{translationFields.map((field) => {
															const languageName = getLanguageName(
																field.languageCode,
																i18n.language
															);
															const currentValue = getValues(`options.${index}`);
															const defaultValue =
																currentValue.translations?.find(
																	({ languageCode }) =>
																		languageCode === field.languageCode
																)?.text || '';
															return (
																<li
																	key={`option-${field.id}-${field.languageCode}`}
																	className="flex flex-row gap-2 align-items-center"
																>
																	<div className="min-w-2-5rem">
																		<Tag variant="grey">
																			{field.languageCode.toUpperCase()}
																		</Tag>
																	</div>
																	<InputText
																		placeholder={t(
																			'programPanel.feedback.optionTranslationPlaceholder',
																			{
																				language: languageName,
																			}
																		)}
																		className="w-full text-sm p-1"
																		defaultValue={defaultValue}
																		onChange={({ currentTarget: { value } }) => {
																			const currentValue = getValues(
																				`options.${index}`
																			);
																			const translations = (
																				currentValue.translations || []
																			)
																				.reduce<
																					Array<{
																						languageCode: string;
																						text: string;
																					}>
																				>(
																					(
																						current,
																						{ languageCode, text }
																					) => {
																						if (
																							current.findIndex(
																								({ languageCode }) =>
																									languageCode ===
																									field.languageCode
																							) === -1
																						) {
																							current.push({
																								languageCode,
																								text,
																							});
																						}
																						return current;
																					},
																					[
																						{
																							languageCode:
																								field.languageCode,
																							text: value,
																						},
																					]
																				)
																				.filter(({ text }) => text.length > 0);
																			setValue(`options.${index}`, {
																				...currentValue,
																				translations,
																			});
																		}}
																	/>
																</li>
															);
														})}
													</ul>
												)}
											</div>

											<ButtonGroup
												buttons={[
													{
														key: 'up',
														icon: 'chevron-up',
														label: t('actions.moveToUp'),
														variant: 'primary-text',
														onClick: () => handleMoveOption(index, 'up'),
														disabled: index === 0,
													},
													{
														key: 'down',
														icon: 'chevron-down',
														label: t('actions.moveToDown'),
														variant: 'primary-text',
														onClick: () => handleMoveOption(index, 'down'),
														disabled: index === optionFields.length - 1,
													},
													{
														key: 'remove',
														icon: 'trash',
														label: t('actions.remove'),
														variant: 'danger-text',
														onClick: () => removeOption(index),
														disabled: optionFields.length === 1,
													},
												]}
												variantSize="sm"
												iconOnly
												className="mt-2"
											/>
										</li>
									);
								})}
							</ul>

							<div className="flex flex-row gap-2 align-items-center">
								<div className="min-w-2-5rem" />
								<Button
									label={t('programPanel.feedback.addOption')}
									icon="plus"
									variant="primary-outlined"
									variantSize="sm"
									onClick={handleAddOptionClick}
									className="align-self-start"
									disabled={
										optionFields.length > 0 && !formOptions?.[formOptions.length - 1]?.text?.trim()
									}
								/>
							</div>
						</div>
					</>
				)}

				<div className="flex flex-column gap-2 mt-3">
					<Controller
						name="required"
						control={control}
						render={({ field: { value, onChange } }) => (
							<label className="flex flex-row gap-2 align-items-center">
								<Checkbox checked={value} onChange={(e) => onChange(e.checked)} />
								<span className="cursor-pointer">{t('programPanel.feedback.requiredQuestion')}</span>
							</label>
						)}
					/>
				</div>
			</form>
		</DialogAction>
	);
};
